<template>
    <div class="mb-2xl">
        <template v-if="activeProduct">
            <v-container class="mb-xl md:grid md:auto-rows-auto md:grid-cols-12">
                <!-- Breadcrumbs-->
                <div class="col-span-5 col-start-8 row-start-1 shrink-0 px-sm pt-sm md:px-0 md:pt-lg">
                    <v-breadcrumbs type="product"
                        :product="activeProduct" />
                </div>

                <!--
                Badges & product images
                Row 1, col 1 - 7
                -->

                <div class="relative col-span-6 col-start-1 row-span-full mt-lg pb-sm md:flex md:pb-0">
                    <div class="md:sticky md:bottom-0 md:flex-1 md:self-end">
                        <v-page-product-gallery
                            class="md:sticky md:top-24 md:min-h-[calc(100vh-1.5rem)]"
                            :active-product="activeProduct"
                            :medias="medias"
                            :price="activeProduct.pricing.price"
                            :promotion-price="activeProduct.pricing.promotionPrice"
                            :original-price="activeProduct.pricing.originalPrice"
                            :promotion="primaryPromotion"
                            :attributes="listingAttributes"
                            :product-badges="productBadges" />
                    </div>
                </div>

                <!--
                    Product details
                    Name, ident, price, discounted price, part payment
                    Row 1, col 8 - 12
                -->
                <div class="col-span-5 col-start-8 row-[2/-1] space-y-lg px-sm pb-lg pt-md md:mt-xl md:px-0">
                    <div class="flex flex-col items-start justify-between gap-xs space-y-xs">
                        <v-h1 tag="h1"
                            no-style
                            lang="fi">
                            <span class="flex items-center justify-between space-x-md">
                                <v-h4 class="m-0 block max-w-full hyphens-auto break-words uppercase"
                                    tag="span"
                                    lang="fi">
                                    {{ activeProduct.title || activeProduct.name }} &nbsp;
                                </v-h4>
                            </span>

                            <span v-if="activeProduct.specification"
                                class="m-0 block pt-2 text-base normal-case leading-4 tracking-xl">
                                {{ activeProduct.specification }}
                            </span>
                        </v-h1>

                        <v-page-product-rating-stars-small
                            :skus="skus"
                            @trigger="isRatingOpen = true" />
                    </div>

                    <v-page-product-price :error="vuelidate.$error"
                        :pending="!productDetailsLoaded"
                        :active-product="activeProduct"
                        :dimensions="dimensions"
                        :promotion-info="promotionInfo"
                        :lowest-price="activeProduct.pricing.lowestPrice.amount"
                        :hide-price-history="promotionInfo.hidePriceHistory"
                        :omnibus-content="promotionInfo.omnibusContent" />

                    <div class="space-y-2">
                        <v-page-product-disclaimer v-for="disclaimer in activeProduct.disclaimers.slice(0, 2)"
                            :key="disclaimer.id"
                            :disclaimer="disclaimer" />
                    </div>

                    <!-- Product configurator -->
                    <v-page-product-configurator v-if="productConfiguration"
                        v-model:selected-product="configurableProduct"
                        v-model:selected-properties="selectedProperties"
                        :configuration="productConfiguration"
                        :product-properties="product?.properties || []" />

                    <!-- Add to cart/wishlist-->
                    <div class="mb-lg flex flex-row space-x-xs">
                        <v-button v-if="canAddToCart"
                            :theme="checkoutButtonTheme"
                            class="flex-1"
                            tabindex="0"
                            :aria-label="t('button.add-to-cart')"
                            @click="addToCart">
                            {{ t('button.add-to-cart') }}
                            <span v-if="productDetails?.cart.multiplier && productDetails?.cart.multiplier > 1">
                                {{ productDetails?.cart.multiplier }} {{ t('button.quantity') }}
                            </span>
                        </v-button>

                        <v-button v-else-if="activeProduct.inventory.state === ProductInventoryState.OutOfStock"
                            theme="gray"
                            class="flex-1 cursor-not-allowed"
                            disabled>
                            {{ t('button.only-from-outlet') }}
                        </v-button>

                        <v-button v-else-if="activeProduct.inventory.state === ProductInventoryState.AvailableInOutlet"
                            theme="gray"
                            class="flex-1 cursor-not-allowed"
                            disabled>
                            {{ t('button.only-from-outlet') }}
                        </v-button>

                        <v-button v-else-if="vuelidate.$errors.length > 0"
                            theme="gray"
                            class="flex-1 cursor-not-allowed"
                            :aria-label="t('button.add-to-cart-disabled')"
                            disabled>
                            {{ t('button.add-to-cart-disabled') }}
                        </v-button>

                        <v-button v-else
                            theme="gray"
                            class="flex-1 cursor-not-allowed"
                            :aria-label="t('button.add-to-cart-disabled')"
                            disabled>
                            {{ t('button.only-from-outlet') }}
                        </v-button>

                        <v-button theme="gray"
                            :aria-label="t('wishlist')"
                            tabindex="-1"
                            @click="addToWishlist">
                            <v-icon icon="heart"
                                class="h-md"></v-icon>
                        </v-button>
                    </div>

                    <div class="flex flex-col divide-y divide-gray-100 rounded-2xs border border-gray-100">
                        <!--
                            1. Delivery time estimate
                            2. Shipping price
                            3. Outlet availability
                        -->
                        <v-page-product-availability :product="activeProduct"
                            :product-details="productDetails"
                            :promotion-info="promotionInfo" />

                        <v-page-product-shop-picker :product="activeProduct"
                            :product-details="productDetails" />
                    </div>

                    <!-- Short description of the product -->
                    <!-- eslint-disable vue/no-v-html -->
                    <div v-if="shortDescription"
                        class="prose"
                        v-html="shortDescription"></div>

                    <!-- Product's additional information -->
                    <div>
                        <client-only>
                            <v-page-product-details :attribute-values="attributeValues"
                                :description="productDetails?.description ?? fullDescription ?? undefined"
                                :dimensions="dimensions"
                                :parcels="parcels"
                                :sku="sku"
                                :assets="assets"
                            />
                        </client-only>

                        <v-page-product-rating-stars
                            :skus="skus"
                            @click="isRatingOpen = true"
                            @keypress="isRatingOpen = true" />
                    </div>
                </div>
            </v-container>

            <!-- Recommendations, row 2 -->
            <v-page-product-alternative-recommendations-list
                v-if="(alternativeRecommendations.before && alternativeRecommendations.before.productGroups.length > 0)
                    && (alternativeRecommendations.after && alternativeRecommendations.after.productGroups.length > 0)"
                :before="alternativeRecommendations.before"
                :after="alternativeRecommendations.after"
                class="mb-xl" />

            <template v-for="list in otherRecommendationLists"
                :key="list.id">
                <v-page-product-recommendation-list v-if="list.productGroups.length > 0"
                    :list="list"
                    class="mb-xl" />
            </template>

            <!-- Product info modals -->
            <v-page-product-rating-modal :is-open="isRatingOpen"
                :sku="activeProduct.sku || ''"
                :skus="skus"
                @close="isRatingOpen = false"></v-page-product-rating-modal>
        </template>

        <v-container v-else
            class="mb-xl md:grid md:auto-rows-auto md:grid-cols-12">
            <div class="px-sm pt-sm md:col-span-5 md:col-start-8 md:px-0 md:pt-lg">
                <v-placeholder class="mb-8 h-6 rounded bg-gray-50 via-gray-100 md:mb-0" />
            </div>

            <div class="col-span-6 row-span-2 pb-sm md:pb-0">
                <v-placeholder class="aspect-square rounded bg-gray-50 before:via-gray-100 md:-mt-6" />
            </div>

            <div class="col-span-5 col-start-8 px-sm pb-lg pt-sm md:px-0">
                <v-placeholder class="mb-1 h-10 w-1/3 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="mb-6 h-6 w-2/4 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="mb-2 h-16 w-32 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="mb-8 h-4 w-1/3 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="mb-8 h-14 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="mb-10 h-52 rounded bg-gray-50 before:via-gray-100" />

                <v-placeholder class="h-24 rounded bg-gray-50 before:via-gray-100" />
            </div>
        </v-container>

        <client-only>
            <!-- Blog section, row 3 -->
            <div v-if="articleData.length > 0"
                class="mb-lg bg-gray-50">
                <!-- Related articles to product from blog -->
                <v-container class="py-lg">
                    <v-scrollable class="mb-md flex snap-x snap-proximity flex-nowrap space-x-md px-sm sm:px-0
                            md:mb-0 md:grid md:grid-cols-3 md:gap-md md:space-x-0">
                        <v-page-product-article v-for="article in articleData"
                            :key="article.uuid"
                            :article="article" />
                    </v-scrollable>
                </v-container>
            </div>
        </client-only>
    </div>
</template>

<script setup lang="ts">
import { asyncComputed, watchDebounced, whenever } from '@vueuse/core';
import { useGtm } from '@gtm-support/vue-gtm';
import useVuelidate from '@vuelidate/core';
import type { ProductPage } from '@apptus/esales-api';
import { useClientHandle, useQuery } from '@urql/vue';
import { marked } from 'marked';
import { getVoyadoKey } from '~~/utils/product';
import { useToast } from '~~/store/toast';
import { formatISO } from 'date-fns';
import sanitizeHtml from 'sanitize-html';
import type {
    ProductKind,
    BlokAsset,
} from '~~/types';
import {
    type SimpleProductDataFragment,
    type ViewProductDetailsQuery,
    type ViewProductQuery,
    type ViewProductDetailsQueryVariables,
    type ConfigurableProductDataFragment,
    type ProductMediaDataFragment,
    ProductInventoryState,
    ProductState,
    ViewProductDetailsDocument,
    ViewProductDocument,
    type ViewProductQueryVariables,
    type ProductAttributeDataFragment,
    ProductMediaOrientation,
    ProductMediaShotType,
    ViewProductShippingMethodsDocument,
    type ViewProductShippingMethodsQuery,
    type ViewProductShippingMethodsQueryVariables,
    ShippingMethodType,
} from '~~/graphql';
import type { RecommendationListSettings } from '~/composables/use-elevate';

type Product = ProductKind<ViewProductQuery['product']>;

type DetailProduct = Extract<
    ViewProductDetailsQuery['product'],
    { __typename: 'SimpleProduct' }
>;

type ProductAttributeValue = ProductAttributeDataFragment;
type ProductParcel = DetailProduct['parcels'][0];
type Promotion = DetailProduct['promotions'][0];
type ProductBadge = DetailProduct['productBadges'][0];

const isRatingOpen = ref(false);
const route = useRoute();

const {
    public: {
        outletGroupingAttributeId,
    },
} = useRuntimeConfig();

const gtmValues = useGtmValues();

const vuelidate = useVuelidate();

/** Product information data fetching
 * Product is fetched by using item ID from route params.
 * Product type is checked.
 * Data get's assigned to computed property.
 * defaultChildProduct value is checked from configurable product.
 * defaultChildProduct product data is fetched if eaerlier check is true.
 * defaultChildProduct product data is assigned to computed properties and after that rendered on the page.
 */

const productSlug = route.params.product.toString();

const { t } = useI18n();
const { t: tg } = useI18n({ useScope: 'global' });
const gtm = useGtm();
const clientHandle = useClientHandle();

const isMounted = ref(false);

const { $i18n } = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);

const checkoutButtonTheme = computed<'black' | 'checkout'>(() => (
    tg('elements.checkout-button') === 'black' ? 'black' : 'checkout'
));

// Note: This is performed only once, not when selecting configuration attributes for a configurable product. It's also awaited, we don't try to render anything before we have this data. Earlier our logic was a bit more complicated and there may be remnants of that left. Much of our product info on the page doesn't change during client-side usage - it's mostly just `configurableProduct` and `activeProduct` that can dynamically change.
const { data } = await clientHandle.useQuery<ViewProductQuery, ViewProductQueryVariables>({
    query: ViewProductDocument,
    variables: {
        slug: productSlug,
    },
});

const product = ref<Product | undefined>(
    (data.value?.product.__typename === 'SimpleProduct'
        || data.value?.product.__typename === 'ConfigurableProduct') ? data.value.product : undefined,
);

if (product.value?.state === ProductState.Draft) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true });
}

const parentProduct = ref<ConfigurableProductDataFragment | undefined>(
    product.value?.__typename === 'ConfigurableProduct'
        ? product.value
        : product.value?.parentProduct ?? undefined,
);

const defaultChildProduct = ref<SimpleProductDataFragment | undefined>(
    parentProduct.value?.configuration.products.find((item) => (
        item.id === parentProduct.value?.configuration.defaultProduct?.id
    )),
);

const configurableProduct = ref<SimpleProductDataFragment>();
const selectedProperties = ref<Record<string, string>>({});

whenever(data, (value) => {
    if (configurableProduct.value) {
        return;
    }

    if (value?.product.__typename === 'SimpleProduct' && value?.product.parentProduct) {
        configurableProduct.value = value.product;
    } else if (product.value?.__typename === 'ConfigurableProduct') {
        configurableProduct.value = defaultChildProduct.value;
    }
}, { immediate: true });

const activeProduct = ref<SimpleProductDataFragment | undefined>(
    parentProduct.value || product.value?.__typename === 'ConfigurableProduct'
        ? configurableProduct.value || defaultChildProduct.value
        : product.value,
);

whenever(configurableProduct, (value) => {
    activeProduct.value = value;
});

const sku = computed(() => {
    if (activeProduct.value && activeProduct.value.sku) {
        return activeProduct.value.sku;
    }

    return undefined;
});

const skus = computed(() => {
    if (parentProduct.value) {
        return parentProduct.value.configuration.products
            .map((item) => item.sku)
            .filter((item): item is string => !!item);
    }

    if (activeProduct.value && activeProduct.value.sku) {
        return [activeProduct.value.sku];
    }

    return [];
});

whenever(activeProduct, (value) => {
    if (import.meta.server) {
        return;
    }

    const url = new URL(window.location.href);

    url.pathname = `/${tg('storyblok.product')}/${value.seo.slug}`;

    // Circumvent nuxt routing to prevent the page from scrolling to the top by vue-router when the URL path changes. Nuxt still thinks we're on the initial variant's page if we do it this way. NOTE that this has at least the side effect that if you select a variant, the URL gets changed, and then you navigate away from the page and back using your browser's back button, nuxt will return you to the initial variant's page rather than the last selected variant / configuration since it isn't aware of the URL changes that happened. Shouldn't cause any other problems.
    window.history.replaceState(null, '', url.pathname + url.search);
    // NOTE: We could have used nuxt's `router.options` `scrollBehavior` config, which configures the underlying vue-router (see the link below). However, `scrollBehavior` overwrites vue-router's whole default scrolling behavior globally, which means it's hard to make scroll retaining work exactly like it works by default (I think the browser manages it by default) - for example, when we used the solution suggested by stackoverflow, pressing the "Load more" button on product listings caused the page to scroll to the top, which didn't happen natively. So that option seems too risky.
    // https://stackoverflow.com/questions/73049943/scroll-behaviour-not-working-in-nuxt3-application/74447828#74447828
    // router.replace(url.pathname + url.search);
}, { immediate: true });

// Fetch benefit image from Storyblok
const benefits = useStoryblokStory('global/product-benefits', { version: 'published' });
const benefitImage = computed(() => benefits.value?.content?.image);

// Check if the benefits is published or not and if there is image defined or not.
const hasBenefitImage = computed(() => !!benefitImage.value && !!benefits.value?.content?.image.filename);

type Media = SimpleProductDataFragment['medias'][0];

/** Transform image object.
 * In order to get image from Storblok to work among with product images we have to transform it to Media object.
 */
function transformImageObject(object: BlokAsset): Media {
    return {
        __typename: 'ProductImage',
        id: object.id,
        title: object.alt,
        url: object.filename || '',
        height: 1280,
        width: 1920,
        shotType: ProductMediaShotType.WhiteBackground,
        orientation: ProductMediaOrientation.Landscape,
        position: 999, // 999 to make sure it's last.
        isDefault: false,
    };
}

// Now transform the benefit image has on the rule hasBenefitImage. If either of those are missing, the benefit image is not transformed. If the benefit image is transformed, it's added to the end of the list.
const transformedBenefitImage = computed(() => (hasBenefitImage.value
    ? transformImageObject(benefitImage.value)
    : undefined));

const medias = computed<ProductMediaDataFragment[]>(() => {
    const activeProductMedias = activeProduct.value?.medias || defaultChildProduct.value?.medias || [];
    const parentProductMedias = parentProduct.value?.medias || [];
    const transformedImage = computed(() => (transformedBenefitImage.value ? [transformedBenefitImage.value] : []));

    return [
        ...activeProductMedias,
        ...activeProductMedias.length > 0
            ? parentProductMedias.map((item) => ({
                ...item,
                isDefault: false,
                position: item.position + 100,
            }))
            : parentProductMedias,
        ...transformedImage.value,
    ];
});

const productDetails = ref<DetailProduct | undefined>();
// Currently refreshing details (due to activeProduct change or initial load)?
const productDetailsBeingFetched = ref(true);
// Current product's details loaded?
const productDetailsLoaded = ref(false);

// onMounted(() => {
//     // Set initial pending state on mounting the product page.
//     productDetailsBeingFetched.value = true;
//     productDetailsLoaded.value = false;
// });

watchDebounced(activeProduct, async (value) => {
    if (!value) {
        return;
    }

    const response = await clientHandle.client.query<ViewProductDetailsQuery, ViewProductDetailsQueryVariables>(
        ViewProductDetailsDocument,
        {
            id: value.id,
            outletBundleAttributeId: outletGroupingAttributeId,
            loadOutletBundleAttributes: !!outletGroupingAttributeId,
        },
    );
    productDetails.value = response.data?.product.__typename === 'SimpleProduct'
        ? response.data.product
        : undefined;
    productDetailsBeingFetched.value = false;
}, {
    debounce: 500,
    immediate: true,
});

watch(activeProduct, (value, oldValue) => {
    // Reset these when our active product changes
    if (value?.id !== oldValue?.id) {
        productDetailsBeingFetched.value = true;
        productDetailsLoaded.value = false;
    }
});

watchEffect(() => {
    if (!activeProduct.value) {
        return;
    }
    const isSimpleProduct = activeProduct.value.__typename === 'SimpleProduct';
    // We have loaded the product's details if the fetch is done and we either have the detail info or our product isn't simple and thus doesn't have details.
    if (!productDetailsBeingFetched.value && (productDetails.value || !isSimpleProduct)) {
        productDetailsLoaded.value = true;
    }
});

const attributeValues = computed<ProductAttributeValue[]>(() => productDetails.value?.attributes || []);
const listingAttributes = computed<ProductAttributeValue[]>(() => productDetails.value?.listingAttributes || []);
const parcels = computed<ProductParcel[]>(() => productDetails.value?.parcels || []);
const dimensions = computed<DetailProduct['dimensions'] | undefined>(() => productDetails.value?.dimensions);
const categories = computed(() => (
    productDetails.value?.categories.map((category) => category.title.toLowerCase()) || []
));
const productBadges = computed<ProductBadge[]>(() => activeProduct.value?.productBadges.slice(0, 5) || []);

const productConfiguration = computed(() => (
    product.value?.__typename === 'ConfigurableProduct'
        ? product.value.configuration
        : product.value?.parentProduct?.configuration
));

// Fetch articles from StoryBlok
const { data: articlesResponse } = useStoryblokArticles({
    /* eslint-disable @typescript-eslint/naming-convention */
    perPage: 3,
    filters: computed(() => ({
        rooms_article_category: { in_array: categories.value?.join(',') ?? '' },
    })),
    /* eslint-enable @typescript-eslint/naming-convention */
});
const articleData = computed(() => articlesResponse.value?.stories || []);

const cart = useCart();
const wishlist = useCart(true);
const toast = useToast();
const { $eventbus } = useNuxtApp();
const { getProductPage } = useElevate();

const gaProduct = computed(() => activeProduct.value && {
    ...gtmValues.productToGa(activeProduct.value, 1),
    ...Object.fromEntries(categories.value?.slice(0, 5).map((category, index) => [
        `item_category${index > 0 ? index + 1 : ''}`,
        category,
    ]) || []),
});

/** Purchace logic */
async function addToCart() {
    if (!activeProduct.value?.id || !gaProduct.value) {
        return;
    }

    try {
        const cartResponse = await cart.addProduct(activeProduct.value.id);
        const cartResponseData = cartResponse.data?.cartProductCreate;
        const cartResponseOk = cartResponseData?.__typename === 'CartProductCreatePayload';

        const cartData = cartResponseOk ? cartResponseData.cart : undefined;

        let gaCartProducts: ReturnType<typeof gtmValues.productToGa>[] = [];
        if (cartData) {
            gaCartProducts = cartData.products.map((cartProduct) => (
                gtmValues.productToGa(cartProduct.product, cartProduct.quantity)
            ));
        }

        gtm?.trackEvent({
            /* eslint-disable @typescript-eslint/naming-convention */
            event: 'add_to_cart',
            _clear: true,
            ecommerce: {
                currency: 'EUR',
                value: gaProduct.value.price,
                items: [gaProduct.value],
                ...gtmValues.cart.value,
            },
            /* eslint-enable @typescript-eslint/naming-convention */
        });

        gtm?.trackEvent({
            /* eslint-disable @typescript-eslint/naming-convention */
            event: 'va_add_to_cart',
            _clear: true,
            ecommerce: {
                currency: 'EUR',
                value: cartData ? Number.parseFloat(cartData.price.amount) : 0,
                items: gaCartProducts,
                ...gtmValues.cart.value,
            },
            /* eslint-enable @typescript-eslint/naming-convention */
        });

        $eventbus.emit('product-added-to-cart', {
            productId: activeProduct.value.id,
        });
    } catch {
        toast.add('warning', `${t('toast.cart-warning')}`);
    }
}

const canAddToCart = computed(() => (
    vuelidate.value.$errors.length === 0 && activeProduct.value?.inventory.isAvailableForPurchase
));

/** Wishlist logic */
async function addToWishlist() {
    if (!activeProduct.value?.id || !gaProduct.value) {
        return;
    }

    gtm?.trackEvent({
        /* eslint-disable @typescript-eslint/naming-convention */
        event: 'add_to_wishlist',
        _clear: true,
        ecommerce: {
            currency: 'EUR',
            value: gaProduct.value.price,
            items: [gaProduct.value],
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    });

    try {
        await wishlist.addProduct(activeProduct.value.id);

        $eventbus.emit('product-added-to-wishlist', {
            productId: activeProduct.value.id,
        });
    } catch {
        toast.add('warning', `${t('toast.wishlist-warning')}`);
    }
}

const recommendationLists = ref<ProductPage['recommendationLists']>([]);

const alternativeRecommendations = computed<{
    before?: ProductPage['recommendationLists'][0]
    after?: ProductPage['recommendationLists'][0]
}>(() => ({
    before: recommendationLists.value.find((list) => list.id === 'alternatives-before'),
    after: recommendationLists.value.find((list) => list.id === 'alternatives-after'),
}));

const otherRecommendationLists = computed<ProductPage['recommendationLists']>(() => (
    recommendationLists.value.filter((list) => !list.id.startsWith('alternatives'))
));

const gtmEventSent = ref(false); // Flag to track if the GTM event has been sent

watchDebounced([activeProduct, productDetails], ([value, details]) => {
    if (!value || !details || !gaProduct.value || gtmEventSent.value) {
        return;
    }

    gtm?.trackEvent({
        /* eslint-disable @typescript-eslint/naming-convention */
        event: 'view_item',
        _clear: true,
        ecommerce: {
            currency: 'EUR',
            value: gaProduct.value.price,
            items: [gaProduct.value],
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    });
    gtmEventSent.value = true;
}, {
    debounce: 500,
    immediate: true,
});

watch([activeProduct, isMounted], async ([activeProductValue, isMountedValue]) => {
    // Since recommendations aren't awaited to be in the first render, we only start trying to fetch them after mount so that we won't hit SSR-CSR rendering mismatches (hydration errors).
    if (!(isMountedValue && activeProductValue)) {
        return;
    }

    const groups: RecommendationListSettings[] = [
        {
            id: 'alternatives-before',
            algorithm: 'ALTERNATIVES',
            limit: 2,
            productRules: `rule incl price [0, ${activeProduct.value?.pricing.price?.amount ?? 'Infinity'}]`,
        },
        {
            id: 'alternatives-after',
            algorithm: 'ALTERNATIVES',
            limit: 18,
            productRules: `rule incl price [${activeProduct.value?.pricing.price?.amount ?? 'infinity'}, Infinity]`,
        },
        {
            id: 'more-from-series',
            algorithm: 'MORE_FROM_SERIES',
        },
        {
            id: 'style-with',
            algorithm: 'STYLE_WITH',
        },
        {
            id: 'upsell',
            algorithm: 'UPSELL',
        },
        {
            id: 'recently-viewed',
            algorithm: 'RECENTLY_VIEWED',
        },
    ];

    try {
        const response = await getProductPage(
            getVoyadoKey(
                'product',
                // If the active product is visible, it will have its data in Voyado at the `product` level. If it's hidden, it's a variant of a parent product (at the `variant` level in Voyado) and its parent will be at the `product` level.
                activeProductValue.state === ProductState.Published || !activeProductValue.parentProduct
                    ? activeProductValue.id
                    : activeProductValue.parentProduct.id,
            ),
            groups.map((settings) => ({
                limit: 20,
                visualization: 'GRID',
                label: t(`product-recommendations.${settings.id.replace(/-(before|after)$/, '')}`),
                ...settings,
            })),
        );

        recommendationLists.value = response.recommendationLists;
    } catch {
        //
    }
}, { immediate: true });

interface PriorityGroup {
    priority: number
    promotions: Promotion[]
}

const priorityGroups = computed<PriorityGroup[]>(() => (
    (productDetails.value?.promotions || [])
        .reduce<PriorityGroup[]>((accumulator, promotion) => {
            const priority = promotion.priority || 0;
            const group = accumulator.find((item) => item.priority === priority);

            if (group) {
                group.promotions.push(promotion);
            } else {
                accumulator.push({
                    priority,
                    promotions: [
                        promotion,
                    ],
                });
            }
            return accumulator;
        }, [])
        .sort((a, b) => b.priority - a.priority)
));

interface PromotionInfo {
    activeTo?: Date
    hidePriceHistory: boolean
    showEndingTimer: boolean
    showCountdownTextContent: boolean
    promotionTimerContent: string
    deliveryPricingInfoOverride: string
    omnibusContent: string
    isTimedVisibility: boolean
    isCampaignModalEnabled: boolean
    campaignModalContent: string
}

const primaryPromotion = computed<Promotion | undefined>(() => priorityGroups.value[0]?.promotions[0]);

const promotionInfo = computed<PromotionInfo>(() => {
    const info: PromotionInfo = {
        activeTo: undefined,
        hidePriceHistory: primaryPromotion.value?.style?.productPage.hidePriceHistory || false,
        showEndingTimer: primaryPromotion.value?.style?.productPage.showPromotionEndingTimer || false,
        showCountdownTextContent: primaryPromotion.value?.style?.productPage.showCountdownTextContent || false,
        promotionTimerContent: primaryPromotion.value?.style?.productPage.promotionTimerContent || '',
        deliveryPricingInfoOverride: primaryPromotion.value?.style?.productPage.deliveryPricingInfoOverride || '',
        omnibusContent: primaryPromotion.value?.style?.productPage.omnibusContent || '',
        isTimedVisibility: !!primaryPromotion.value?.style?.productPage.isTimedVisibility,
        isCampaignModalEnabled: !!primaryPromotion.value?.style?.productPage.isCampaignModalEnabled,
        campaignModalContent: primaryPromotion.value?.style?.productPage.campaignModalContent || '',
    };

    if (info.showEndingTimer && primaryPromotion.value?.activeTo) {
        info.activeTo = new Date(primaryPromotion.value.activeTo);
    } else {
        priorityGroups.value.forEach((group) => {
            group.promotions.forEach((promotion) => {
                if (promotion.activeTo && new Date(promotion.activeTo) > new Date()) {
                    info.activeTo = new Date(promotion.activeTo);
                }
            });
        });
    }

    return info;
});

const shortDescription = asyncComputed<string | undefined>(async () => {
    if (activeProduct.value?.shortDescription) {
        return marked.parse(activeProduct.value.shortDescription);
    }

    if (parentProduct.value?.shortDescription) {
        return marked.parse(parentProduct.value.shortDescription);
    }

    return undefined;
});

const fullDescription = asyncComputed<string | undefined>(async () => {
    if (parentProduct.value?.description) {
        return marked.parse(parentProduct.value.description);
    }

    return undefined;
});

const assets = computed(() => {
    if (parentProduct.value !== undefined && parentProduct.value.assets.length > 0) {
        return parentProduct.value.assets;
    }

    return activeProduct.value?.assets || [];
});

const { clubPrice } = useClubPromotion(activeProduct, 'product');

const currentPrice = computed(() => (
    clubPrice.value ?? Number(activeProduct.value?.pricing.price?.amount)
));

onMounted(() => {
    isMounted.value = true;
});

definePageMeta({
    alias: [
        '/tuote/:product',
        '/toote/:product',
        '/toode/:product',
    ],
});

/** Schema structure */
const seo = useSeoUrl();
const seoUrl = ref('');

watch(() => activeProduct.value, async (value) => {
    if (value) {
        seoUrl.value = seo.useUrl(value?.seo?.slug || '');
    }
}, { immediate: true });

// Sanitize the description for SEO meta tags.
const descriptionsForSanitization = ref(activeProduct.value?.shortDescription || parentProduct.value?.shortDescription);

// Sanitize the description for SEO meta tags.
const sanitizedDescription = computed(() => sanitizeHtml(descriptionsForSanitization.value ?? '', {
    allowedTags: [],
}).replaceAll('\n', ' '));

// This switch statement is used to determine the availability of the product for Schema.org.
const getJsonldAvailability = (schemaProduct: SimpleProductDataFragment) => {
    switch (schemaProduct.inventory.state) {
        case ProductInventoryState.InStock: {
            return schemaProduct.inventory.deliveryEstimateDays !== null
                && schemaProduct.inventory.deliveryEstimateDays <= 7
                ? 'https://schema.org/InStock' : 'https://schema.org/BackOrder';
        }
        case ProductInventoryState.AvailableInOutlet: {
            return 'https://schema.org/InStoreOnly';
        }
        case ProductInventoryState.AvailableToOrder: {
            return 'https://schema.org/BackOrder';
        }
        default: {
            return 'https://schema.org/OutOfStock';
        }
    }
};

const {
    getRating,
} = useReviews();

// These are used for the JSON-LD schema.
const { rating, totalRatingCount } = getRating(skus.value);

// Maximum delivery estimate is the delivery estimate days + 5 days.
// This is only used for the JSON-LD schema.
const maximumDeliveryEstimate = computed(() => {
    const deliveryEstimateDays = activeProduct.value?.inventory.deliveryEstimateDays;
    return deliveryEstimateDays == null ? 0 : deliveryEstimateDays + 5;
});

// Minimum delivery estimate is the delivery estimate days - 2 days.
// This is only used for the JSON-LD schema.
const minimumDeliveryEstimate = computed(() => {
    const deliveryEstimateDays = activeProduct.value?.inventory.deliveryEstimateDays;
    return deliveryEstimateDays == null ? 0 : deliveryEstimateDays - 2;
});

const postalCode = ref('00100');
// Fetch the shipping methods for the product.
const { data: shippingData } = useQuery<ViewProductShippingMethodsQuery, ViewProductShippingMethodsQueryVariables>({
    query: ViewProductShippingMethodsDocument,
    variables: computed(() => ({
        id: activeProduct.value?.id || '',
        postalCode: postalCode.value,
    })),
});

// Get the shipping methods from the shipping data.
const shippingMethods = computed(() => (
    shippingData.value?.product.__typename === 'SimpleProduct'
        ? shippingData.value.product.availableShippingMethods
        : []
));

// Get the cheapest delivery price from the shipping methods.
// This value is then used in the JSON-LD schema.
const cheapestDeliveryPrice = computed<number | undefined>(() => (
    (shippingMethods.value
        .filter((method) => ![ShippingMethodType.PickUpFromStore].includes(method.type))
        .map((method) => Number(method.originalPriceWithServices.amount))
        .sort((a, b) => a - b)
        .at(0)
    )
));

// Check if product is eligible for free shipping. This is used in the JSON-LD schema.
const isEligibleForFreeShipping = computed(() => (
    shippingData.value?.product.__typename === 'SimpleProduct'
        ? shippingData.value.product.isEligibleForFreeShipping
        : false
));

/* eslint-disable @typescript-eslint/naming-convention */
useJsonld(() => {
    if (!activeProduct.value) {
        return null;
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: activeProduct.value.name ?? parentProduct.value?.name,
        description: shortDescription.value,
        image: activeProduct.value.medias[0]?.url ?? parentProduct.value?.medias[0]?.url,
        sku: activeProduct.value.sku ?? undefined,
        brand: activeProduct.value.brand ? {
            '@type': 'Brand',
            name: activeProduct.value.brand.title ?? undefined,
            url: activeProduct.value.brand.url ?? undefined,
            logo: activeProduct.value.brand.logo ?? undefined,
        } : undefined,
        offers: {
            '@type': 'Offer',
            price: currentPrice.value,
            priceCurrency: activeProduct.value.pricing.price?.currencyCode,
            availability: getJsonldAvailability(activeProduct.value),
            priceValidUntil: promotionInfo.value.activeTo ? formatISO(promotionInfo.value.activeTo) : undefined,
            url: seoUrl.value,
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                merchantReturnDays: 14,
                inStoreReturnsOffered: 'https://schema.org/True',
                refundType: 'https://schema.org/FullRefund',
                merchantReturnLink: currentLang.value === 'fi'
                    ? 'https://www.masku.com/vaihdot-ja-palautukset'
                    : 'https://www.masku.ee/umbervahetamine-ja-tagastamine',
                returnFees: 'https://schema.org/FreeReturn',
                returnMethod: ['https://schema.org/ReturnByMail', 'https://schema.org/ReturnInStore'],
                applicableCountry: {
                    '@type': 'Country',
                    name: t('schema.country'),
                },
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    name: t('schema.country'),
                },
                shippingRate: {
                    '@type': 'MonetaryAmount',
                    value: isEligibleForFreeShipping.value ? 0 : cheapestDeliveryPrice.value,
                    currency: 'EUR',
                },
                deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    handlingTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 0,
                        maxValue: 1,
                        unitCode: 'DAY',
                    },
                    transitTime: {
                        '@type': 'QuantitativeValue',
                        minValue: minimumDeliveryEstimate.value, // Not sure if this is correct
                        maxValue: maximumDeliveryEstimate.value,
                        unitCode: 'DAY',
                    },
                },
            },
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: rating.value,
            reviewCount: totalRatingCount.value,
        },
    };
});
/* eslint-enable @typescript-eslint/naming-convention */

useSeoMeta({
    title: activeProduct.value?.name ?? parentProduct.value?.name,
    description: () => sanitizedDescription.value,
    ogTitle: () => activeProduct.value?.name ?? parentProduct.value?.name,
    ogDescription: () => sanitizedDescription.value,
    ogImage: () => medias.value[0]?.url ?? undefined,
    twitterCard: () => 'summary_large_image',
});

/**
 * Computes the canonical URL for the product page
 *
 * The canonical URL is determined based on either the parent product's or active product's SEO slug.
 * If a parent product exists, its slug is used; otherwise, the active product's slug is used.
 *
 * @computed
 * @returns {string} The canonical URL for the product
 */
const canonicalUrl = computed(() => {
    if (parentProduct.value) {
        return seo.useUrl(parentProduct.value?.seo?.slug);
    }

    return seo.useUrl(activeProduct.value?.seo?.slug);
});

useHead({
    meta: [
        { name: 'keywords', content: activeProduct.value?.seo?.keywords ?? '' },
    ],
    link: [
        { rel: 'canonical', href: canonicalUrl },
    ],
});
</script>

<i18n lang="json">
{
    "et": {
        "wishlist": "Soovinimekiri",
        "button": {
            "add-to-cart": "Lisa ostukorvi",
            "add-to-cart-disabled": "Valik tegemata",
            "cannot-add-to-cart": "Hetkel laost otsas",
            "out-of-stock": "Hetkel laost otsas",
            "only-from-outlet": "Saadaval vaid kohapeal",
            "quantity": "tk"
        },
        "lowest-price-in-the-last-30-days": "30 päeva madalaim hind",
        "product-recommendations": {
            "style-with": "Sarnase stiiliga tooted",
            "upsell": "Ostetud ka lisaks",
            "more-from-series": "Veel samast seeriast",
            "recently-viewed": "Viimati vaadatud",
            "alternatives": "Sarnased tooted"
        },
        "promotion-active-to": "Pakkumine kehtib kuni {date} või kuniks kaupa jätkub",
        "promotion-active-until": "Pakkumine kehtib kuni {date}",
        "schema": {
            "country": "EE"
        }
    },
    "fi": {
        "wishlist": "Muistilista",
        "button": {
            "add-to-cart": "Lisää ostoskoriin",
            "add-to-cart-disabled": "Tee valinnat",
            "cannot-add-to-cart": "Loppu varastosta",
            "out-of-stock": "Tuote loppunut",
            "only-from-outlet": "Vain myymälöistä",
            "quantity": "kpl"
        },
        "lowest-price-in-the-last-30-days": "Alin hinta viim. 30 pv aikana",
        "product-recommendations": {
            "style-with": "Tyyliin sopivia tuotteita",
            "upsell": "Täydennä kokonaisuus",
            "more-from-series": "Samasta tuoteperheestä",
            "recently-viewed": "Katsomasi tuotteet",
            "alternatives": "Vaihtoehtoja"
        },
        "promotion-active-to": "Hinta voimassa {date} asti tai niin kauan kuin tuotetta riittää.",
        "promotion-active-until": "Hinta voimassa vähintään {date} asti.",
        "schema": {
            "country": "FI"
        }
    }
}
</i18n>
